/**
 * Form Input Components
 * A collection of reusable form input components with validation and styling
 */

import React from "react";

/**
 * Generic text input component
 * @param {Object} args - Component props
 * @param {Function} args.register - React Hook Form register function
 * @param {string} args.placeholder - Input placeholder text
 * @param {Object} args.errors - Form errors object
 * @param {string} args.name - Input field name
 * @param {string} args.label - Input label text
 * @param {boolean} args.isRequired - Whether the field is required
 * @param {string} args.type - Input type (defaults to 'text')
 * @returns {JSX.Element} Rendered text input component
 */
export const inputText = (args) => {
  const {
    register,
    placeholder,
    errors,
    name,
    label,
    isRequired = true,
    type = "text",
  } = args;

  return (
    <div className="mb-4">
      <label htmlFor={name} className="block font-medium text-white mb-1">
        {label}
      </label>
      <input
        {...register(name, {
          required: isRequired ? `Inserire ${label}` : null,
        })}
        type={type}
        placeholder={placeholder}
        className="form-control mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
      />
      {errors[name] && (
        <span className="text-red-500 text-xs mt-1">
          {errors[name].message}
        </span>
      )}
    </div>
  );
};

/**
 * Email input component with validation
 * @param {Object} args - Component props passed to inputText
 * @returns {JSX.Element} Rendered email input component
 */
export const inputEmail = (args) =>
  inputText({
    ...args,
    type: "email",
    placeholder: "Enter your email",
    pattern: {
      value: /\S+@\S+\.+\S+/,
      message: "Entered value does not match email format",
    },
  });

/**
 * Password input component with validation
 * @param {Object} args - Component props
 * @param {Function} args.register - React Hook Form register function
 * @param {string} args.placeholder - Input placeholder text
 * @param {Object} args.errors - Form errors object
 * @param {Function} args.watch - React Hook Form watch function
 * @param {string} args.name - Input field name
 * @param {string} args.label - Input label text
 * @param {boolean} args.validate - Whether to validate against another password field
 * @param {boolean} args.isRequired - Whether the field is required
 * @param {number} args.minLength - Minimum password length
 * @returns {JSX.Element} Rendered password input component
 */
export const inputPassword = (args) => {
  const {
    register,
    placeholder,
    errors,
    watch,
    name,
    label,
    validate = false,
    isRequired = true,
    minLength = 6,
  } = args;

  return (
    <div className="mb-4">
      <label htmlFor={name} className="block font-medium text-white mb-1">
        {label}
      </label>
      <input
        {...register(name, {
          required: isRequired ? `Inserire ${label}` : null,
          minLength: {
            value: minLength,
            message: `La password deve avere almeno ${minLength} caratteri`,
          },
          validate: validate
            ? (value) => value === watch().password || "Password errata"
            : null,
        })}
        type="password"
        placeholder={placeholder}
        className="form-control mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
      />
      {errors[name] && (
        <span className="text-red-500 text-xs mt-1">
          {errors[name].message}
        </span>
      )}
    </div>
  );
};

/**
 * Country select input component
 * @param {Object} props - Component props
 * @param {Function} props.register - React Hook Form register function
 * @param {string} props.label - Input label text
 * @param {string} props.name - Input field name
 * @param {Function} props.onChange - Change handler function
 * @param {string} props.selectedOption - Currently selected option
 * @param {Object} props.errors - Form errors object
 * @param {Array} props.countries - Array of country options
 * @param {string} props.placeholder - Select placeholder text
 * @param {boolean} props.isRequired - Whether the field is required
 * @returns {JSX.Element} Rendered country select component
 */
export const InputCountry = ({
  register,
  label,
  name,
  onChange,
  selectedOption,
  errors,
  countries,
  placeholder,
  isRequired = true,
}) => (
  <div className="mb-4">
    <label htmlFor={name} className="block font-medium text-white mb-1">
      {label}
    </label>
    <select
      {...register(name, {
        required: isRequired ? `Seleziona il tuo ${label}` : null,
      })}
      value={selectedOption}
      onChange={(e) => onChange(e.target.value)}
      className="form-control mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
    >
      <option value="">{placeholder}</option>
      {countries.map((country) => (
        <option key={country.value} value={country.value}>
          {country.label}
        </option>
      ))}
    </select>
    {errors[name] && (
      <span className="text-red-500 text-xs mt-1">{errors[name].message}</span>
    )}
  </div>
);

/**
 * City select input component
 * @param {Object} props - Component props
 * @param {Function} props.register - React Hook Form register function
 * @param {string} props.label - Input label text
 * @param {string} props.name - Input field name
 * @param {Function} props.onChange - Change handler function
 * @param {string} props.selectedOption - Currently selected option
 * @param {Object} props.errors - Form errors object
 * @param {Array} props.cities - Array of city options
 * @param {boolean} props.isRequired - Whether the field is required
 * @returns {JSX.Element} Rendered city select component
 */
export const InputCity = ({
  register,
  label,
  name,
  onChange,
  selectedOption,
  errors,
  cities,
  isRequired = true,
}) => (
  <div className="mb-4">
    <label htmlFor={name} className="block font-medium text-white mb-1">
      {label}
    </label>
    <select
      {...register(name, {
        required: isRequired ? `Seleziona la tua ${label}` : null,
      })}
      value={selectedOption}
      onChange={(e) => onChange(e.target.value)}
      className="form-control mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
    >
      <option value="">Select a city</option>
      {cities.map((city, index) => (
        <option key={index} value={city.value}>
          {city.label}
        </option>
      ))}
    </select>
    {errors[name] && (
      <span className="text-red-500 text-xs mt-1">{errors[name].message}</span>
    )}
  </div>
);

/**
 * Address input component
 * @param {Object} props - Component props
 * @param {Function} props.register - React Hook Form register function
 * @param {string} props.label - Input label text
 * @param {string} props.name - Input field name
 * @param {string} props.value - Input field value
 * @param {Function} props.onChange - Change handler function
 * @param {string} props.placeholder - Input placeholder text
 * @param {Object} props.errors - Form errors object
 * @param {boolean} props.isRequired - Whether the field is required
 * @returns {JSX.Element} Rendered address input component
 */
export const InputAddress = ({
  register,
  label,
  name,
  value,
  onChange,
  placeholder,
  errors,
  isRequired = true,
}) => (
  <div className="mb-4">
    <label htmlFor={name} className="block font-medium text-white mb-1">
      {label}
    </label>
    <input
      {...register(name, {
        required: isRequired ? `Inserisci il tuo ${label} di casa` : null,
      })}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="form-control mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
    />
    {errors[name] && (
      <span className="text-red-500 text-xs mt-1">{errors[name].message}</span>
    )}
  </div>
);
